import GenericDB from './generic-db';

export default class ServiceCodesDB extends GenericDB {
  constructor() {
    super('serviceCodes');
  }

  /**
   * TWO POPULATIONS IN ONE COLLECTION, and neither read reaches both.
   *
   * The MANAGED catalog carries no `clinic` field at all — one shared set every clinic sees
   * identically — while a clinic's ORG-AUTHORED codes carry a bare `clinic` like every other scoped
   * collection here. So a caller wanting everything a clinic can reference runs two equality
   * queries, `origin == 'managed'` and `clinic == <id>`, and unions them. One query cannot do it: a
   * document missing a filtered field is EXCLUDED, not returned last, so `clinic ==` never sees the
   * catalog.
   *
   * WHICH IS WHY THE GUARD BELOW IS NOT THE SAME GUARD AS ITS SIBLINGS. `FundingSourcesDB` refuses a
   * read with no `clinic` constraint, because for that collection an unscoped read reaches every
   * clinic's data. Here an unscoped read reaches every clinic's CUSTOM codes, which is the same
   * leak — but requiring `clinic` specifically would make the catalog unreadable. So the rule is
   * that a read must narrow on one of the two, and only the composed reader in
   * `contexts/revenueCycle/infrastructure/api/serviceCodeApi` should be doing either.
   *
   * The rules already refuse a cross-clinic read of a custom document, so on the client an unscoped
   * query fails as an opaque permissions error. The guard makes it name the actual mistake, on the
   * same terms as `FundingSourcesDB`.
   *
   * WRITES ARE `custom` ONLY, and that is the rules' constraint rather than this class's. A managed
   * code is seeded and maintained through the admin SDK; the rules gate every client create and
   * update on `origin == 'custom'` and on the clinic that owns it, so a managed code stays unwritable
   * from a browser whatever this class exposes. Removal is the platform soft-delete trio — there is
   * no hard delete, because a code something references must stay resolvable or every reference
   * becomes a permanent hold with no record of what it meant.
   *
   * @param {Array<Array>} constraints - must narrow on `clinic` or on `origin`
   */
  readAll(constraints = null, ...rest) {
    const isNarrowed = Array.isArray(constraints)
      && constraints.some((constraint) => ['clinic', 'origin'].includes(constraint?.[0]));

    if (!isNarrowed) {
      throw new Error(
        'serviceCodes holds the global catalog and every clinic\'s own codes: reads must narrow on '
        + "['origin', '==', 'managed'] or ['clinic', '==', clinicId].",
      );
    }

    return super.readAll(constraints, ...rest);
  } // readAll()
}
